import React, {useEffect} from 'react';
import PropTypes from 'prop-types';
import Flickity from 'react-flickity-component';
import {formatMoney} from '@shopify/theme-currency';

import {imageUrl} from '../../../../assets/scripts/utils/shopify-images';
import iconPlus from '../../../../assets/svg/icon-plus.svg';
import iconGift from '../../../../assets/svg/icon-gift.svg';

import {isGiftQualified} from './index';

const GiftWithPurchase = ({
  gifts,
  excludedGifts,
  claimedGifts,
  remainingSpend,
  cartItems,
  addToCart,
  updateLineItems,
  gwpManuallyRemoved,
  setGwpManuallyRemoved,
}) => {
  const addGWPProductToCart = async (variantId) => {
    if (!variantId) {
      return;
    }

    const item = {
      id: variantId,
      quantity: 1,
      properties: {
        _gwp: true,
      },
    };
    await addToCart(item, false);
  };

  const removeItemsFromCart = async (items) => {
    const updates = {};

    items.forEach((item) => {
      updates[item.key] = 0;
    });

    await updateLineItems(updates);
  };

  const minRemainingSpend = remainingSpend.length ? formatMoney(Math.min(...remainingSpend)) : 0;

  const gwpHeaderTitle = () => {
    if (!claimedGifts.length && !gifts.length) {
      return window.theme.strings.giftWithPurchase.remainingSpend.replace('{{ amount }}', minRemainingSpend);
    } else if (gifts.length) {
      return window.theme.strings.giftWithPurchase.redeemPrompt;
    } else if (claimedGifts.length && minRemainingSpend) {
      return window.theme.strings.giftWithPurchase.additionalRemainingSpend.replace('{{ amount }}', minRemainingSpend);
    } else {
      return window.theme.strings.giftWithPurchase.giftsRedeemed;
    }
  };

  useEffect(() => {
    const invalidGifts = claimedGifts.filter((gift) => !isGiftQualified(gift, gifts, cartItems));

    if (invalidGifts.length === 0) return;

    const invalidGiftsIds = invalidGifts.map((gift) => gift.product_id);

    const itemsToRemove = cartItems.filter((item) => item.properties._gwp && invalidGiftsIds.includes(item.product_id));

    if (itemsToRemove.length === 0) return;

    removeItemsFromCart(itemsToRemove);
  }, [claimedGifts]);

  useEffect(() => {
    // Auto add first gift if enabled from metafields
    if (gwpManuallyRemoved) return;

    const firstGift = gifts[0];
    if (!firstGift || !firstGift.auto_add_enabled) return;

    const firstGiftVariant = firstGift.variants.find((variant) => variant.available);
    if (!firstGiftVariant) return;

    addGWPProductToCart(firstGiftVariant.id);
    setGwpManuallyRemoved(false);
  }, [gifts]);

  return (
    <div className="gwp">
      <div className="gwp__header">
        <h3 className="body-small">
          <span aria-hidden="true" dangerouslySetInnerHTML={{__html: iconGift}} />
          <span dangerouslySetInnerHTML={{__html: gwpHeaderTitle()}} />
        </h3>
        {Boolean(excludedGifts.length && gifts.length) && (
          <p>
            {window.theme.strings.giftWithPurchase.additionalRemainingSpend.replace('{{ amount }}', minRemainingSpend)}
          </p>
        )}
      </div>

      {gifts.length ? (
        <Flickity
          className="gwp__slider dots--small"
          elementType="div"
          options={{
            resize: false,
            cellAlign: 'left',
            pageDots: true,
            prevNextButtons: false,
            contain: true,
          }}
          disableImagesLoaded={false}
          reloadOnUpdate
          static
        >
          {gifts.map((product) => {
            const {variants, title, image, id, handle} = product;
            const firstAvailableVariant = variants.find((variant) => variant.available === true);
            return (
              <div key={id} className="gwp__slide">
                <div class="gwp-product">
                  <a href={`/products/${handle}`} className="aspect-ratio aspect-ratio--square">
                    {image && (
                      <img
                        src={imageUrl(image, 160, 240, 'center')}
                        width={80}
                        height={120}
                        alt={title}
                        loading="lazy"
                      />
                    )}
                  </a>

                  <div className="gwp-product__content">
                    <h4 className="gwp-product__title body-small">{title}</h4>
                    <div className="gwp-product__price body-small">{window.theme.strings.cartFree}</div>

                    {variants.length > 1 ? (
                      <a href={`/products/${handle}`} className="gwp-product__button">
                        {window.theme.strings.giftWithPurchase.view}
                      </a>
                    ) : (
                      <button
                        type="button"
                        className="gwp-product__button btn-none"
                        onClick={() => addGWPProductToCart(firstAvailableVariant?.id)}
                      >
                        {window.theme.strings.giftWithPurchase.add}
                        <span dangerouslySetInnerHTML={{__html: iconPlus}} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </Flickity>
      ) : (
        ''
      )}
    </div>
  );
};

GiftWithPurchase.propTypes = {
  gifts: PropTypes.arrayOf(PropTypes.object),
  excludedGifts: PropTypes.arrayOf(PropTypes.object),
  claimedGifts: PropTypes.arrayOf(PropTypes.object),
  remainingSpend: PropTypes.arrayOf(PropTypes.number),
  cartItems: PropTypes.arrayOf(PropTypes.object),
  addToCart: PropTypes.func,
  updateLineItems: PropTypes.func,
  gwpManuallyRemoved: PropTypes.bool,
  setGwpManuallyRemoved: PropTypes.func,
};

export default GiftWithPurchase;
